// javascript
import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import WarningIcon from '@mui/icons-material/Warning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GavelIcon from '@mui/icons-material/Gavel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PercentIcon from '@mui/icons-material/Percent';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import cambioEstadoService from '../services/cambioEstadoService';
import { subirArchivo } from '../services/archivoService';
import EventoService from '../services/eventoService';
import fechasUtils from '../utils/fechasUtils';
import ModalConfirmacionCambioEstado from '../components/ModalConfirmacionCambioEstado';

// Función auxiliar para obtener la fecha actual en formato YYYY-MM-DD
const obtenerFechaActual = () => {
    return fechasUtils.formatDateISO(new Date());
};

function GestorEstadoPrimeraApelacion({ tramite, onEstadoCambiado }) {
    const [cargando, setCargando] = useState(false);
    const [mostrarFormularioApelacion, setMostrarFormularioApelacion] = useState(false);
    // const [historialEstados, setHistorialEstados] = useState([]); // Eliminado por no uso
    const [cargandoHistorial, setCargandoHistorial] = useState(true);
    const [infoDictamen, setInfoDictamen] = useState(null);
    const [diasRestantes, setDiasRestantes] = useState(null);
    const [colorAlerta, setColorAlerta] = useState('secondary');
    const [vencido, setVencido] = useState(false);
    const [feriados, setFeriados] = useState([]);
    const [mostrarModalCerrarTramite, setMostrarModalCerrarTramite] = useState(false);

    // Estados del formulario
    const [datosApelacion, setDatosApelacion] = useState({
        fechaNotificacion: obtenerFechaActual(),
        porcentaje: '',
        fechaLimite: '',
        motivo: '',
        fechaPresentacionCMC: obtenerFechaActual(),
        archivoApelacion: null
    });

    const [archivos, setArchivos] = useState([]);
    const [subiendoArchivos, setSubiendoArchivos] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const cargarDatos = async () => {
            // Cargar feriados primero
            try {
                const feriadosData = await fechasUtils.obtenerFeriadosActivos();
                setFeriados(feriadosData);
            } catch (error) {
                console.error('Error al cargar feriados:', error);
            }

            cargarHistorial();

            // Calcular fecha límite inicial si hay fecha de notificación
            if (datosApelacion.fechaNotificacion && !datosApelacion.fechaLimite) {
                const fechaLimite = await calcularFechaLimiteConFeriados(datosApelacion.fechaNotificacion);
                setDatosApelacion(prev => ({ ...prev, fechaLimite }));
            }
        };

        cargarDatos();
    }, [tramite.idTramite]);

    useEffect(() => {
        if (infoDictamen && infoDictamen.fechaLimite) {
            calcularDiasRestantes();
            const interval = setInterval(() => {
                calcularDiasRestantes();
            }, 60000); // Actualizar cada minuto

            return () => clearInterval(interval);
        }
    }, [infoDictamen]);

    // Efecto para mostrar notificaciones cuando el plazo está por vencer (solo una vez)
    useEffect(() => {
        if (diasRestantes !== null && !vencido && diasRestantes <= 2 && diasRestantes >= 0) {
            // Usar un timeout para evitar notificaciones múltiples
            const timeoutId = setTimeout(() => {
                if (diasRestantes === 2) {
                    toast.warning(`⚠️ Alerta: El trámite tiene 2 días hábiles restantes para apelar. Fecha límite: ${infoDictamen?.fechaLimite || 'N/A'}`, {
                        autoClose: 10000
                    });
                } else if (diasRestantes === 1) {
                    toast.error(`🚨 URGENTE: El trámite tiene 1 día hábil restante para apelar. Fecha límite: ${infoDictamen?.fechaLimite || 'N/A'}`, {
                        autoClose: 15000
                    });
                } else if (diasRestantes === 0) {
                    toast.error(`🔴 CRÍTICO: ¡ÚLTIMO DÍA! El plazo para apelar vence hoy: ${infoDictamen?.fechaLimite || 'N/A'}`, {
                        autoClose: 20000
                    });
                }
            }, 1000); // Pequeño delay para evitar múltiples notificaciones

            return () => clearTimeout(timeoutId);
        }
    }, [diasRestantes, vencido]); // Removido infoDictamen de dependencias para evitar loops

    const cargarHistorial = async () => {
        setCargandoHistorial(true);
        try {
            const historial = await cambioEstadoService.getHistorialEstados(tramite.idTramite);
            // setHistorialEstados(historial); // Eliminado

            // Buscar el cambio de estado que contiene la información del dictamen (estado 7 -> 9)
            // O buscar en el historial el último comentario que menciona "Porcentaje de incapacidad"
            const cambioDictamen = historial
                .filter(c => c.comentario && c.comentario.includes('Porcentaje de incapacidad'))
                .pop();

            if (cambioDictamen && cambioDictamen.comentario) {
                parsearInfoDictamen(cambioDictamen.comentario);
            }
        } catch (error) {
            console.error("Error al cargar historial:", error);
            toast.error("No se pudo cargar la información del dictamen.");
        } finally {
            setCargandoHistorial(false);
        }
    };

    const sincronizarEventoVencimiento = async (fechaDate, tipoApelacion) => {
        if (!fechaDate || !tramite.idTramite) return;

        try {
            // Formatear fecha para el evento
            const fechaNotificacionStr = fechasUtils.formatDateISO(fechaDate); // YYYY-MM-DD
            const tituloEvento = `Vencimiento ${tipoApelacion}`;
            const descripcion = `Vencimiento automático calculado para la ${tipoApelacion} del trámite #${tramite.idTramite}`;
            // Recordatorios: 2 días, 1 día, 2 horas antes
            const tiemposRecordatorio = [48, 24, 2];

            // Obtener eventos existentes del trámite usando el método correcto
            const respuesta = await EventoService.getEventosPorTramite(tramite.idTramite);
            const eventos = respuesta.data || [];

            // Buscar si ya existe este evento específico (por título y tipo)
            // Asumiendo que el backend devuelve tipoEvento o tipo
            const eventoExistente = eventos.find(e =>
                e.titulo === tituloEvento &&
                (e.tipoEvento === 'Vencimiento' || e.tipo === 'Vencimiento')
            );

            if (eventoExistente) {
                // Verificar si la fecha coincide
                // El evento existente tendrá fecha en formato YYYY-MM-DD seguramente
                const fechaExistente = eventoExistente.fecha;

                if (fechaExistente !== fechaNotificacionStr) {
                    console.log(`Actualizando evento de vencimiento: ${fechaExistente} -> ${fechaNotificacionStr}`);
                    await EventoService.modificarEvento(eventoExistente.id || eventoExistente.idEvento, {
                        ...eventoExistente,
                        fecha: fechaNotificacionStr,
                        horaInicio: '08:00',
                        horaFin: '14:00',
                        tiemposRecordatorio // Actualizar recordatorios también si hiciera falta
                    });
                    toast.info(`📅 Fecha de vencimiento actualizada en el calendario: ${fechaNotificacionStr}`);
                }
            } else {
                // Crear nuevo evento usando el método correcto y estructura correcta
                console.log("Creando nuevo evento de vencimiento en calendario");
                await EventoService.crearEvento({
                    titulo: tituloEvento,
                    descripcion: descripcion,
                    fecha: fechaNotificacionStr,
                    horaInicio: '08:00',
                    horaFin: '14:00',
                    todoElDia: false,
                    tipoEvento: 'Vencimiento',
                    idTramite: tramite.idTramite,
                    visibilidad: 'POR_AREA',
                    canalesNotificacion: {
                        whatsapp: false,
                        email: false,
                        inapp: true
                    },
                    tiemposRecordatorio
                });
                toast.success(`📅 Evento de vencimiento creado en el calendario para el ${fechaNotificacionStr}`);
            }

        } catch (error) {
            console.error("Error al sincronizar evento de vencimiento:", error);
        }
    };

    const eliminarEventoVencimiento = async (tipoApelacion) => {
        try {
            const tituloEvento = `Vencimiento ${tipoApelacion}`;
            const respuesta = await EventoService.getEventosPorTramite(tramite.idTramite);
            const eventos = respuesta.data || [];

            const eventoExistente = eventos.find(e =>
                e.titulo === tituloEvento &&
                (e.tipoEvento === 'Vencimiento' || e.tipo === 'Vencimiento')
            );

            if (eventoExistente) {
                await EventoService.eliminarEvento(eventoExistente.id || eventoExistente.idEvento);
                console.log(`🗑️ Evento de vencimiento eliminado automáticamente: ${tituloEvento}`);
            }
        } catch (error) {
            console.error("Error al eliminar evento de vencimiento:", error);
            // No bloqueamos el flujo si falla la eliminación
        }
    };

    const parsearInfoDictamen = async (comentario) => {
        const info = {
            porcentaje: null,
            fechaNotificacion: null,
            fechaLimite: null,
            resultado: null
        };

        // Extraer porcentaje
        const porcentajeMatch = comentario.match(/Porcentaje de incapacidad: ([\d.]+)%/);
        if (porcentajeMatch) {
            info.porcentaje = parseFloat(porcentajeMatch[1]);
        }

        // Extraer fecha de notificación
        const fechaNotMatch = comentario.match(/Fecha de notificación: (.+?)(?:\n|$)/);
        if (fechaNotMatch) {
            info.fechaNotificacion = fechaNotMatch[1].trim();
        }

        // Extraer resultado
        const resultadoMatch = comentario.match(/Resultado: (.+?)(?:\n|$)/);
        if (resultadoMatch) {
            info.resultado = resultadoMatch[1].trim();
        }

        // RECALCULO OBLIGATORIO DE FECHA LÍMITE
        // Ignoramos la fecha límite que pueda venir en el texto si podemos recalcularla correctamente
        // Esto corrige errores históricos donde se calculó mal la fecha.
        if (info.fechaNotificacion) {
             const fechaNotificacionNormalizada = convertirFechaTextoADateInput(info.fechaNotificacion);
             if (fechaNotificacionNormalizada) {
                 const fechaDate = new Date(fechaNotificacionNormalizada + 'T00:00:00');
                 if (!isNaN(fechaDate.getTime())) {
                     try {
                        const feriadosActivos = await fechasUtils.obtenerFeriadosActivos();
                        console.log("Feriados recuperados para cálculo:", feriadosActivos);

                        const fechaCalc = await fechasUtils.calcularDiasHabiles(fechaDate, 5, feriadosActivos);
                        // Guardamos en formato largo para visualización
                        const fechaCalcStr = fechasUtils.formatDateLong(fechaCalc);
                        const fechaCalcISO = fechasUtils.formatDateISO(fechaCalc); // Para logs o uso interno si hiciera falta

                        console.log("Recálculo Detallado:", {
                            inicio: fechasUtils.formatDateISO(fechaDate),
                            dias: 5,
                            finCalculado: fechaCalcISO,
                            finFormateado: fechaCalcStr,
                            feriadosCount: feriadosActivos.length
                        });

                        // Sobrescribimos la fecha límite con la calculada correctamente y formateada
                        info.fechaLimite = fechaCalcStr;

                        // Sincronizar con calendario
                        sincronizarEventoVencimiento(fechaCalc, "1° Apelación");

                     } catch(err) {
                         console.error("Error recalculando fecha límite", err);
                         // Fallback: tratar de leer del texto si falló el cálculo
                         const fechaLimiteMatch = comentario.match(/Fecha límite para apelar: (.+?)(?:\n|$)/);
                         if (fechaLimiteMatch) {
                            info.fechaLimite = fechaLimiteMatch[1].trim();
                         }
                     }
                 }
             }
        } else {
            // Fallback si no hay fecha notificación: leer del texto
            const fechaLimiteMatch = comentario.match(/Fecha límite para apelar: (.+?)(?:\n|$)/);
            if (fechaLimiteMatch) {
                info.fechaLimite = fechaLimiteMatch[1].trim();
            }
        }

        setInfoDictamen(info);

        // Pre-llenar formulario si está disponible
        if (info.fechaNotificacion && info.porcentaje) {
            const fechaNotificacionNormalizada = convertirFechaTextoADateInput(info.fechaNotificacion);

            // Usamos la fecha límite que acabamos de calcular (info.fechaLimite ahora tiene el valor correcto)
            let fechaLimiteNormalizada = '';

            if (info.fechaLimite) {
                // Si info.fechaLimite ya es string YYYY-MM-DD (del cálculo), lo usamos
                // Si viene del texto (formato largo), lo convertimos
                if (/^\d{4}-\d{2}-\d{2}$/.test(info.fechaLimite)) {
                    fechaLimiteNormalizada = info.fechaLimite;
                } else {
                    fechaLimiteNormalizada = convertirFechaTextoADateInput(info.fechaLimite);
                }
            }

            // Si aún no tenemos fecha límite (falló todo), intentamos calcular de nuevo (redundante pero seguro)
            if (!fechaLimiteNormalizada && fechaNotificacionNormalizada) {
                const fechaNotificacionDate = new Date(fechaNotificacionNormalizada + 'T00:00:00');
                 if (!isNaN(fechaNotificacionDate.getTime())) {
                    fechasUtils.calcularDiasHabiles(fechaNotificacionDate, 5, feriados.length > 0 ? feriados : null)
                        .then(fechaLimiteCalculada => {
                            fechaLimiteNormalizada = fechasUtils.formatDateISO(fechaLimiteCalculada);
                            setDatosApelacion(prev => ({
                                ...prev,
                                fechaNotificacion: fechaNotificacionNormalizada,
                                porcentaje: info.porcentaje.toString(),
                                fechaLimite: fechaLimiteNormalizada
                            }));
                        });
                    return;
                 }
            }

            setDatosApelacion(prev => ({
                ...prev,
                fechaNotificacion: fechaNotificacionNormalizada,
                porcentaje: info.porcentaje.toString(),
                fechaLimite: fechaLimiteNormalizada
            }));
        }
    };

    const convertirFechaTextoADateInput = (fechaTexto) => {
        // Intenta convertir fechas en formato español a formato YYYY-MM-DD
        // Formato esperado: "lunes, 15 de enero de 2024" o variaciones
        try {
            // Intentar parsear directamente con Date si es una fecha ISO
            const fechaISO = new Date(fechaTexto);
            if (!isNaN(fechaISO.getTime())) {
                return fechasUtils.formatDateISO(fechaISO);
            }

            // Si no, intentar parsear formato español
            const partes = fechaTexto.toLowerCase().split(' ');
            if (partes.length >= 3) {
                const meses = {
                    'enero': '01', 'febrero': '02', 'marzo': '03', 'abril': '04',
                    'mayo': '05', 'junio': '06', 'julio': '07', 'agosto': '08',
                    'septiembre': '09', 'octubre': '10', 'noviembre': '11', 'diciembre': '12'
                };

                // Buscar el día (número)
                let dia = null;
                let mes = null;
                let año = null;

                for (let i = 0; i < partes.length; i++) {
                    const parte = partes[i].replace(',', '').replace('de', '').trim();

                    // Buscar día (número entre 1 y 31)
                    if (!dia && /^\d{1,2}$/.test(parte)) {
                        const num = parseInt(parte);
                        if (num >= 1 && num <= 31) {
                            dia = num.toString().padStart(2, '0');
                        }
                    }

                    // Buscar mes
                    if (!mes && meses[parte]) {
                        mes = meses[parte];
                    }

                    // Buscar año (4 dígitos)
                    if (!año && /^\d{4}$/.test(parte)) {
                        año = parte;
                    }
                }

                if (dia && mes && año) {
                    return `${año}-${mes}-${dia}`;
                }
            }
        } catch (e) {
            console.error("Error al convertir fecha:", e);
        }
        return '';
    };

    // Función helper para calcular fecha límite con feriados
    const calcularFechaLimiteConFeriados = async (fechaNotif) => {
        if (!fechaNotif) return '';
        const fecha = new Date(fechaNotif + 'T00:00:00');
        if (isNaN(fecha.getTime())) return '';

        try {
            const fechaLimite = await fechasUtils.calcularDiasHabiles(fecha, 5, feriados.length > 0 ? feriados : null);
            return fechasUtils.formatDateISO(fechaLimite); // Devolver fecha local formateada
        } catch (error) {
            console.error('Error al calcular fecha límite:', error);
            return '';
        }
    };

// Función local para calcular días hábiles (Eliminada)
    // const calcularDiasHabiles = (fechaInicio, dias) => {
    //    // ...
    // };

    const calcularDiasRestantes = () => {
        if (!infoDictamen || !infoDictamen.fechaLimite) return;

        try {
            // Intentar parsear la fecha límite desde el formato de texto
            const fechaLimiteTexto = infoDictamen.fechaLimite;
            let fechaLimiteDate = parsearFechaDesdeTexto(fechaLimiteTexto);

            // Si no se pudo parsear, intentar calcular desde fecha de notificación como fallback
            if (!fechaLimiteDate && infoDictamen.fechaNotificacion) {
                 const fechaNot = parsearFechaDesdeTexto(infoDictamen.fechaNotificacion);
                 if (fechaNot) {
                    // Usamos la versión síncrona si feriados están cargados, o el fallback local
                     if (feriados.length > 0) {
                        fechaLimiteDate = fechasUtils.calcularDiasHabilesSync(fechaNot, 5, feriados);
                     } else {
                        // Fallback básico si no hay feriados cargados (mejor esperar a que carguen, pero por si acaso)
                         fechaLimiteDate = new Date(fechaNot);
                         let diasAgregados = 0;
                         while (diasAgregados < 5) {
                             fechaLimiteDate.setDate(fechaLimiteDate.getDate() + 1);
                             if (fechaLimiteDate.getDay() !== 0 && fechaLimiteDate.getDay() !== 6) {
                                 diasAgregados++;
                             }
                         }
                     }
                 }
            }

            if (!fechaLimiteDate) return;

            const hoy = new Date();
            hoy.setHours(0, 0, 0, 0);
            fechaLimiteDate.setHours(0, 0, 0, 0);

            // Si la fecha límite ya pasó
            if (hoy > fechaLimiteDate) {
                 const diffTime = fechaLimiteDate - hoy;
                 const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                 actualizarEstadoContador(diffDays);
                 return;
            }

            // Calcular días hábiles restantes desde mañana hasta la fecha límite
            const manana = new Date(hoy);
            manana.setDate(manana.getDate() + 1);

            const listaFeriados = (feriados && Array.isArray(feriados)) ? feriados : [];
            const diasHabilesRestantes = fechasUtils.contarDiasHabilesSync(manana, fechaLimiteDate, listaFeriados);

            actualizarEstadoContador(diasHabilesRestantes);

        } catch (error) {
            console.error("Error al calcular días restantes:", error);
        }
    };

    const parsearFechaDesdeTexto = (fechaTexto) => {
        try {
            const meses = {
                'enero': 0, 'febrero': 1, 'marzo': 2, 'abril': 3,
                'mayo': 4, 'junio': 5, 'julio': 6, 'agosto': 7,
                'septiembre': 8, 'octubre': 9, 'noviembre': 10, 'diciembre': 11
            };
            const partes = fechaTexto.toLowerCase().split(' ');
            if (partes.length >= 3) {
                const dia = parseInt(partes[0]);
                const mes = meses[partes[2]];
                const año = parseInt(partes[4] || partes[partes.length - 1]);
                if (!isNaN(dia) && mes !== undefined && !isNaN(año)) {
                    return new Date(año, mes, dia);
                }
            }
        } catch (e) {
            console.error("Error al parsear fecha:", e);
        }
        return null;
    };

    const actualizarEstadoContador = (dias) => {
        setDiasRestantes(dias);

        if (dias < 0) {
            // Vencido
            setVencido(true);
            setColorAlerta('secondary'); // Gris
            verificarCierreAutomatico();
        } else if (dias === 0) {
            // Último día
            setVencido(false);
            setColorAlerta('danger'); // Rojo
        } else if (dias <= 2) {
            // 1-2 días
            setVencido(false);
            setColorAlerta('warning'); // Naranja (usando warning)
        } else {
            // > 3 días
            setVencido(false);
            setColorAlerta('warning'); // Amarillo (usando warning también, pero con menor intensidad)
        }
    };

    const verificarCierreAutomatico = async () => {
        // Verificar si el trámite ya está cerrado o cancelado
        if (tramite.idEstadoActual === 13 || tramite.idEstadoActual === 14) {
            return; // Ya está cerrado
        }

        // Aquí podríamos implementar el cierre automático
        // Por ahora, solo mostramos una alerta
        toast.warning("⚠️ El plazo para apelar ha vencido. El trámite debe ser cerrado.");
    };

    const handleSeleccionarArchivos = (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;

        const nuevosArchivos = files.map(file => ({
            id: `local_${Date.now()}_${Math.random()}`,
            file,
            nombre: file.name,
            tamaño: (file.size / (1024 * 1024)).toFixed(2) + " MB",
            estado: "pendiente",
            esDelBackend: false
        }));

        setArchivos(prev => [...prev, ...nuevosArchivos]);
        if (event.target) event.target.value = "";
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
        else if (e.type === "dragleave") setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleSeleccionarArchivos({ target: { files: e.dataTransfer.files } });
        }
    };

    const handleEliminarArchivo = (id) => {
        setArchivos(prev => prev.filter(a => a.id !== id));
    };

    const handleFechaNotificacionChange = async (fecha) => {
        const nuevaFecha = fecha;

        // Actualizar la fecha de notificación inmediatamente
        setDatosApelacion(prev => ({
            ...prev,
            fechaNotificacion: nuevaFecha,
            fechaLimite: '' // Limpiar mientras se calcula
        }));

        if (nuevaFecha) {
            // Asegurarse de que la fecha esté en formato Date válido
            const fechaDate = new Date(nuevaFecha + 'T00:00:00');
            if (!isNaN(fechaDate.getTime())) {
                try {
                    // Calcular fecha límite con feriados
                    const fechaLimiteCalculada = await fechasUtils.calcularDiasHabiles(fechaDate, 5, feriados.length > 0 ? feriados : null);
                    const fechaLimite = fechaLimiteCalculada.toISOString().split('T')[0];
                    console.log('📅 Fecha límite recalculada (con feriados):', fechaLimite, 'desde fecha de notificación:', nuevaFecha);

                    // Actualizar con la fecha límite calculada
                    setDatosApelacion(prev => ({
                        ...prev,
                        fechaLimite: fechaLimite
                    }));
                } catch (error) {
                    console.error('Error al calcular fecha límite:', error);
                }
            }
        }
    };

    const handleRegistrarApelacion = async () => {
        // Validaciones
        if (!datosApelacion.fechaNotificacion) {
            toast.error("La fecha de notificación es obligatoria.");
            return;
        }
        if (!datosApelacion.porcentaje || datosApelacion.porcentaje === '') {
            toast.error("El porcentaje de incapacidad es obligatorio.");
            return;
        }
        if (!datosApelacion.fechaLimite) {
            toast.error("La fecha límite para apelar es obligatoria.");
            return;
        }
        if (!datosApelacion.fechaPresentacionCMC) {
            toast.error("La fecha de presentación CMC es obligatoria.");
            return;
        }
        const archivosPendientes = archivos.filter(a => a.estado === 'pendiente');
        if (archivosPendientes.length === 0) {
            toast.error("Debe adjuntar el archivo de la apelación.");
            return;
        }
        if (!datosApelacion.motivo || datosApelacion.motivo.trim() === '') {
            toast.error("El motivo u observación es obligatorio.");
            return;
        }

        setCargando(true);
        setSubiendoArchivos(true);
        try {
            // Subir archivos
            const archivosSubidos = [];
            for (const archivo of archivosPendientes) {
                try {
                    const archivoSubido = await subirArchivo(tramite.idTramite, archivo.file);
                    archivosSubidos.push(archivoSubido);
                    setArchivos(prev => prev.map(item =>
                        item.id === archivo.id ? { ...item, estado: "subido", idArchivo: archivoSubido.idArchivo, esDelBackend: true } : item
                    ));
                } catch (error) {
                    toast.error(`Error subiendo ${archivo.nombre}`);
                    throw error;
                }
            }
            setSubiendoArchivos(false);

            // Función auxiliar para normalizar fechas al formato YYYY-MM-DD
            const normalizarFecha = (fecha) => {
                if (!fecha) {
                    console.warn('⚠️ Fecha vacía o nula');
                    return null;
                }

                // Si ya está en formato YYYY-MM-DD, devolverla
                if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
                    console.log('✅ Fecha ya en formato correcto:', fecha);
                    return fecha;
                }

                // Si es una fecha en formato texto, convertirla
                const fechaNormalizada = convertirFechaTextoADateInput(fecha);
                if (fechaNormalizada) {
                    console.log('✅ Fecha convertida desde texto:', fecha, '->', fechaNormalizada);
                    return fechaNormalizada;
                }

                // Intentar parsear como Date y convertir a YYYY-MM-DD
                try {
                    // Intentar con diferentes formatos
                    let fechaDate = new Date(fecha);

                    // Si falla, intentar agregar hora para evitar problemas de zona horaria
                    if (isNaN(fechaDate.getTime()) && /^\d{4}-\d{2}-\d{2}$/.test(fecha.trim())) {
                        fechaDate = new Date(fecha + 'T00:00:00');
                    }

                    if (!isNaN(fechaDate.getTime())) {
                        const fechaFormateada = fechaDate.toISOString().split('T')[0];
                        console.log('✅ Fecha parseada desde Date:', fecha, '->', fechaFormateada);
                        return fechaFormateada;
                    }
                } catch (e) {
                    console.warn('⚠️ No se pudo parsear la fecha:', fecha, e);
                }

                console.error('❌ No se pudo normalizar la fecha:', fecha);
                return null;
            };

            // Eventos en el calendario:
            // - El evento de vencimiento se gestionó dinámicamente con sincronizarEventoVencimiento
            // - Al registrar la apelación, eliminamos ese evento de vencimiento pendientes
            // - No creamos eventos históricos de presentación a pedido del usuario.

            // Crear comentario
            const comentario = `Apelación CMC registrada\nFecha de notificación del dictamen: ${formatearFecha(datosApelacion.fechaNotificacion)}\nPorcentaje de incapacidad: ${datosApelacion.porcentaje}%\nFecha límite para apelar: ${formatearFecha(datosApelacion.fechaLimite)}\nFecha de presentación CMC: ${formatearFecha(datosApelacion.fechaPresentacionCMC)}\nMotivo: ${datosApelacion.motivo}\nArchivos adjuntos: ${archivosSubidos.map(a => a.nombre).join(', ')}`;

            // Eliminar evento de vencimiento automático ya que se cumplió la tarea
            await eliminarEventoVencimiento("1° Apelación");

            // Cambiar a estado "Espera Dictamen CMC" (estado 15)
            // Si el estado no existe en la BD, usar el estado 10 (Segunda Apelación) como fallback
            const cambioEstadoData = {
                idEstado: 15, // Espera Dictamen CMC
                comentario: comentario
            };

            try {
                await cambioEstadoService.cambiarEstado(tramite.idTramite, cambioEstadoData);
            } catch (error) {
                // Si el estado 15 no existe, usar el estado 10 como fallback
                console.warn("Estado 15 no encontrado, usando estado 10 como fallback");
                cambioEstadoData.idEstado = 10;
                await cambioEstadoService.cambiarEstado(tramite.idTramite, cambioEstadoData);
            }

            toast.success("Apelación CMC registrada correctamente. El trámite ahora espera el dictamen de la CMC.");

            // Limpiar formulario
            setMostrarFormularioApelacion(false);
            setArchivos([]);
            setDatosApelacion({
                fechaNotificacion: '',
                porcentaje: '',
                fechaLimite: '',
                motivo: '',
                fechaPresentacionCMC: '',
                archivoApelacion: null
            });

            if (onEstadoCambiado) {
                await onEstadoCambiado();
            }

        } catch (error) {
            console.error("Error al registrar apelación:", error);
            toast.error("Error al registrar la apelación: " + (error.response?.data || error.message));
        } finally {
            setCargando(false);
            setSubiendoArchivos(false);
        }
    };

    const formatearFecha = (fechaString) => {
        if (!fechaString) return '';
        const fecha = new Date(fechaString);
        if (isNaN(fecha.getTime())) return '';
        const fechaLocal = new Date(fecha.valueOf() + fecha.getTimezoneOffset() * 60000);
        return fechaLocal.toLocaleDateString('es-ES', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const obtenerClaseColorContador = () => {
        if (vencido) return 'bg-secondary text-white';
        if (diasRestantes === 0) return 'bg-danger text-white';
        if (diasRestantes <= 2) return 'bg-warning text-dark';
        return 'bg-warning text-dark'; // Amarillo para > 3 días
    };

    const obtenerTextoContador = () => {
        if (vencido) return 'VENCIDO';
        if (diasRestantes === 0) return 'ÚLTIMO DÍA';
        if (diasRestantes === 1) return '1 día hábil restante';
        return `${diasRestantes} días hábiles restantes`;
    };

    if (cargandoHistorial) {
        return (
            <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
                <div className="alert alert-info">Cargando información del dictamen...</div>
            </div>
        );
    }

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold text-warning mb-0">
                    <GavelIcon className="me-2" />
                    1° Apelación
                </h4>
                <span className={`badge ${obtenerClaseColorContador()} rounded-pill px-3 py-2`}>
                    <AccessTimeIcon fontSize="small" className="me-1" />
                    {diasRestantes !== null ? obtenerTextoContador() : 'Calculando...'}
                </span>
            </div>

            {vencido && (
                <div className="alert alert-danger border-0 rounded-3">
                    <WarningIcon className="me-2" />
                    <strong>Plazo Vencido:</strong> El plazo para interponer la primera apelación ha vencido.
                    El trámite debe ser cerrado automáticamente.
                </div>
            )}

            {!vencido && diasRestantes !== null && diasRestantes <= 2 && diasRestantes >= 0 && (
                <div className={`alert alert-${colorAlerta} border-0 rounded-3`}>
                    <WarningIcon className="me-2" />
                    <strong>ALERTA - Plazo por Vencer:</strong> Tienes {diasRestantes === 0 ? 'hoy (ÚLTIMO DÍA)' : `${diasRestantes} día(s) hábil(es)`} para interponer la primera apelación ante la Comisión Médica Central (CMC).
                    <br />
                    <small className="mt-2 d-block">Por favor, registre la apelación lo antes posible para evitar el cierre automático del trámite.</small>
                </div>
            )}
            {!vencido && diasRestantes !== null && diasRestantes > 2 && (
                <div className="alert alert-info border-0 rounded-3">
                    <strong>Plazo para Apelar:</strong> Tienes {diasRestantes} día(s) hábiles para interponer la primera apelación ante la Comisión Médica Central (CMC).
                </div>
            )}

            {/* Información del Dictamen */}
            {infoDictamen && (
                <div className="mt-3 p-3 bg-light rounded-3 border">
                    <h6 className="fw-semibold text-secondary mb-3">Información del Dictamen</h6>
                    <div className="row g-3">
                        {infoDictamen.porcentaje !== null && (
                            <div className="col-md-4 d-flex align-items-center">
                                <PercentIcon className="text-primary me-2" />
                                <div>
                                    <small className="text-muted d-block">Porcentaje de Incapacidad</small>
                                    <strong className="text-dark">{infoDictamen.porcentaje}%</strong>
                                </div>
                            </div>
                        )}
                        {infoDictamen.resultado && (
                            <div className="col-md-4 d-flex align-items-center">
                                <CheckCircleIcon className="text-primary me-2" />
                                <div>
                                    <small className="text-muted d-block">Resultado</small>
                                    <strong className="text-dark">{infoDictamen.resultado}</strong>
                                </div>
                            </div>
                        )}
                        {infoDictamen.fechaNotificacion && (
                            <div className="col-md-4 d-flex align-items-center">
                                <CalendarMonthIcon className="text-primary me-2" />
                                <div>
                                    <small className="text-muted d-block">Fecha de Notificación</small>
                                    <strong className="text-dark">{infoDictamen.fechaNotificacion}</strong>
                                </div>
                            </div>
                        )}
                        {infoDictamen.fechaLimite && (
                            <div className="col-md-4 d-flex align-items-center">
                                <AccessTimeIcon className="text-primary me-2" />
                                <div>
                                    <small className="text-muted d-block">Fecha Límite para Apelar</small>
                                    <strong className="text-dark">{infoDictamen.fechaLimite}</strong>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {mostrarFormularioApelacion ? (
                <div className="mt-4">
                    <div className="card shadow-sm border-0 rounded-4 p-4">
                        <h5 className="fw-semibold text-primary mb-3">Registrar Apelación CMC</h5>

                        <div className="row g-3">
                            <div className="col-md-4">
                                <label className="form-label fw-semibold">
                                    Fecha de Notificación del Dictamen <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={datosApelacion.fechaNotificacion}
                                    onChange={e => handleFechaNotificacionChange(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-semibold">
                                    Porcentaje de Incapacidad (%) <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="number"
                                    className="form-control"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={datosApelacion.porcentaje}
                                    onChange={e => setDatosApelacion({ ...datosApelacion, porcentaje: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-semibold">
                                    Fecha Límite para Apelar <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={datosApelacion.fechaLimite}
                                    onChange={e => setDatosApelacion({ ...datosApelacion, fechaLimite: e.target.value })}
                                    required
                                    readOnly
                                />
                                <small className="text-muted">Calculada automáticamente (5 días hábiles)</small>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">
                                    Fecha de Presentación CMC <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={datosApelacion.fechaPresentacionCMC}
                                    onChange={e => setDatosApelacion({ ...datosApelacion, fechaPresentacionCMC: e.target.value })}
                                    required
                                />
                                <small className="text-muted">Fecha en que se presentó la apelación ante la CMC</small>
                            </div>
                            <div className="col-12">
                                <label className="form-label fw-semibold">
                                    Motivo u Observación <span className="text-danger">*</span>
                                </label>
                                <textarea
                                    className="form-control"
                                    rows="4"
                                    value={datosApelacion.motivo}
                                    onChange={e => setDatosApelacion({ ...datosApelacion, motivo: e.target.value })}
                                    placeholder="Describa los motivos de la apelación..."
                                    required
                                />
                            </div>
                        </div>

                        {/* Gestor de Archivos */}
                        <div className="mt-4">
                            <h6 className="fw-semibold text-primary">Archivo de Apelación (PDF) <span className="text-danger">*</span></h6>
                            {archivos.length > 0 && (
                                <div className="list-group mb-3">
                                    {archivos.map(archivo => (
                                        <div key={archivo.id} className="list-group-item d-flex justify-content-between align-items-center">
                                            <div>
                                                <AttachFileIcon fontSize="small" className="me-2 text-muted" />
                                                <span className="fw-semibold">{archivo.nombre}</span>
                                                <small className="text-muted ms-2">({archivo.tamaño})</small>
                                                <span className={`badge ms-2 ${archivo.estado === 'subido' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                    {archivo.estado}
                                                </span>
                                            </div>
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => handleEliminarArchivo(archivo.id)}
                                                title="Eliminar"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div
                                className={`border rounded-3 p-4 text-center cursor-pointer ${dragActive ? "border-primary bg-primary bg-opacity-10" : "border-dashed"}`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleSeleccionarArchivos}
                                    className="d-none"
                                    accept=".pdf,.PDF"
                                />
                                <CloudUploadIcon className="text-muted mb-2" />
                                <p className="mb-0">Arrastra el archivo PDF aquí o haz clic para seleccionar</p>
                                <small className="text-muted">Solo archivos PDF</small>
                            </div>
                        </div>

                        <div className="d-flex justify-content-center gap-3 mt-4">
                            <button
                                className="btn btn-success d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                onClick={handleRegistrarApelacion}
                                disabled={cargando || subiendoArchivos || vencido}
                            >
                                <CheckCircleIcon fontSize="small" />
                                {cargando || subiendoArchivos ? 'Registrando...' : 'Registrar Apelación CMC'}
                            </button>
                            <button
                                className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                onClick={() => setMostrarFormularioApelacion(false)}
                                disabled={cargando || subiendoArchivos}
                            >
                                <CancelIcon fontSize="small" />
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="d-flex justify-content-center flex-wrap gap-3 mt-4 pt-3 border-top">
                    <button
                        className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                        onClick={() => setMostrarFormularioApelacion(true)}
                        disabled={cargando || vencido}
                    >
                        <UploadFileIcon fontSize="small" />
                        Registrar Apelación CMC
                    </button>
                    {vencido && (
                        <>
                            <button
                                className="btn btn-danger d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                onClick={() => setMostrarModalCerrarTramite(true)}
                                disabled={cargando}
                            >
                                <CancelIcon fontSize="small" />
                                Cerrar Trámite (Vencido)
                            </button>
                            <ModalConfirmacionCambioEstado
                                isOpen={mostrarModalCerrarTramite}
                                onClose={() => setMostrarModalCerrarTramite(false)}
                                onConfirm={async () => {
                                    setCargando(true);
                                    try {
                                        const cambioEstadoData = {
                                            idEstado: 13, // Rechazado
                                            comentario: 'Trámite cerrado automáticamente por vencimiento del plazo para apelar (5 días hábiles).'
                                        };
                                        await cambioEstadoService.cambiarEstado(tramite.idTramite, cambioEstadoData);
                                        toast.success("Trámite cerrado por vencimiento del plazo.");
                                        setMostrarModalCerrarTramite(false);
                                        if (onEstadoCambiado) await onEstadoCambiado();
                                    } catch (error) {
                                        console.error("Error al cerrar trámite:", error);
                                        toast.error("Error al cerrar el trámite.");
                                    } finally {
                                        setCargando(false);
                                    }
                                }}
                                titulo="Confirmar Cierre de Trámite"
                                mensaje="¿Confirma que desea cerrar el trámite por vencimiento del plazo?"
                                siguienteEstado="Rechazado"
                                cargando={cargando}
                                textoBotonConfirmar="Cerrar Trámite"
                            />
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default GestorEstadoPrimeraApelacion;
